import { OrganizationType } from '../types/organization.type';
import type { KnowledgeArticle } from '../types/knowledge-base.type';
import {
  buildPermissionsHandbookArticleContent,
  PERMISSIONS_HANDBOOK_ARTICLE_ID,
} from './permissions-handbook';

const ALL_ORGANIZATION_TYPES = [
  OrganizationType.WASTE_OUTLET,
  OrganizationType.CHARITY,
  OrganizationType.TRUCK,
  OrganizationType.RECYCLER,
] as const;

export const KNOWLEDGE_BASE_CATEGORY_LABELS: Record<KnowledgeArticle['category'], string> = {
  technical_support: 'Technical Support',
  dashboard_kpis: 'Dashboard & KPIs',
  organizations_users: 'Organizations & Users',
  complaints: 'Complaints',
  suggestions: 'Suggestions',
  general_inquiry: 'General',
};

export const KNOWLEDGE_BASE_ARTICLES: KnowledgeArticle[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    summary: 'Monitor waste statistics, charts, maps, and KPI progress.',
    content:
      'The Dashboard shows your organization\'s waste operations for each month and over its lifetime: pickup orders, collected materials and, depending on your organization type, branches, containers and fleet routes. Use the tabs, charts and the map to track performance.\n\nSelect a guide below for step-by-step instructions.',
    category: 'dashboard_kpis',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'statistics', 'charts'],
    children: [
      'dashboard-overview',
      'dashboard-monthly-tab',
      'dashboard-organization-overview',
      'customize-dashboard',
      'kpi-limits',
      'download-report',
      'dashboard-maps',
    ],
  },
  {
    id: 'dashboard-overview',
    title: 'Dashboard Overview',
    summary: 'Navigate the dashboard and select your organization.',
    content:
      '1. Open Dashboard in the sidebar (home icon).\n\n2. Select your organization from the sidebar dropdown.\n\n3. Use the Monthly and Organization overview tabs to switch between period-based and lifetime views.\n\n4. Use the header actions to set KPI limits or customize visible metrics.\n\nTip: If no data appears, confirm the correct organization is selected and that operational data exists for the chosen month.',
    category: 'dashboard_kpis',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'overview', 'organization'],
  },
  {
    id: 'dashboard-monthly-tab',
    title: 'Monthly Tab',
    summary: 'View statistics for a selected reporting month.',
    content:
      '1. On the Dashboard, open the Monthly tab.\n\n2. Select a month from the month picker.\n\n3. Review stat cards showing key metrics with trend indicators compared to the previous period.\n\n4. Scroll to view charts for each selected statistic.\n\n5. When KPI limits are enabled, review the KPI progress section at the top of the monthly view.',
    category: 'dashboard_kpis',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'monthly', 'statistics'],
  },
  {
    id: 'dashboard-organization-overview',
    title: 'Organization Overview Tab',
    summary: 'View lifetime performance metrics for your organization.',
    content:
      '1. On the Dashboard, open the Organization overview tab.\n\n2. Review lifetime performance metrics aggregated across all available data.\n\n3. Stat cards show totals without month-over-month trend arrows.\n\n4. Charts and widgets follow what you selected for this tab in Customize Dashboard.\n\nTip: Use this tab for a high-level picture of long-term performance.',
    category: 'dashboard_kpis',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'overview', 'lifetime'],
  },
  {
    id: 'customize-dashboard',
    title: 'Customize Dashboard',
    summary: 'Choose which metrics and chart types appear on your dashboard.',
    content:
      '1. On the Dashboard, click Customize Dashboard. The dialog follows the tab you are on (Monthly or Organization overview).\n\n2. Search or browse every widget for your organization type — including KPI Progress, maps, and operational statistics.\n\n3. Check the items you want to display.\n\n4. Click Save Changes. Your selection is saved per tab for this browser session.\n\nTip: If a chart or map area is empty, open Customize Dashboard on that tab and add the widgets you need.',
    category: 'dashboard_kpis',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'customize', 'charts'],
  },
  {
    id: 'kpi-limits',
    title: 'KPI Limits',
    summary: 'Set targets and track progress on the Monthly dashboard tab.',
    content:
      '1. Open Dashboard in the sidebar.\n\n2. Click KPI Limits in the dashboard header.\n\n3. In the dialog, choose a metric, pick an operator, enter a target value, and add an alert message.\n\n4. Click Add Limit, then enable or disable limits with the switch as needed.\n\n5. Close the dialog. Enabled KPIs appear as progress cards on the Monthly tab.\n\nTip: Use Go to KPI Limits from this article for a short guided walkthrough: first click the button, then fill in the form.',
    category: 'dashboard_kpis',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'kpi', 'targets', 'limits', 'alerts'],
  },
  {
    id: 'download-report',
    title: 'Download Report',
    summary: 'Export your statistics as a PDF report.',
    content:
      '1. Open Report in the sidebar.\n\n2. Choose the report period from the dropdown — pick a specific month or All Time.\n\n3. Click Customize Report to choose which sections appear in the PDF, then save.\n\n4. Click Download Report and wait for the PDF to generate.\n\n5. Save the file from your browser.\n\nThe report includes the sections you selected, such as metric tables, waste breakdowns, and KPI target comparisons when limits are enabled.',
    category: 'dashboard_kpis',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'report', 'pdf', 'export', 'customize'],
  },
  {
    id: 'dashboard-maps',
    title: 'Dashboard Maps',
    summary: 'See branches, containers, route runs and trucks on the TomTom map.',
    content:
      'The dashboard maps use TomTom and show where your operations are.\n\n• Sites & containers (waste outlets and charities, Monthly tab) — your registered branches and containers. Filter by Needs attention, Awaiting pickup or Out of service; select a container to see its fill level and alarms, or a branch to see this month\'s collected waste.\n• Fleet (waste movers) — the day\'s route runs with their planned routes and stop progress, and each truck\'s last reported position. Pick a day, filter by run status and select a run to see its stops.\n\nPositions are registered locations or a truck\'s last report, not a continuous feed. Turn a map on or off from Customize.',
    category: 'dashboard_kpis',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'map', 'containers', 'trucks'],
  },
  {
    id: 'branch-analysis',
    title: 'Branch analysis',
    summary: 'Compare operational performance across branches for waste outlet organizations.',
    content:
      'Branch analysis shows pickup, waste, revenue, and diversion metrics by collection site for the selected month. Available for Waste Outlet organizations.\n\nSelect a guide below for step-by-step instructions.',
    category: 'dashboard_kpis',
    organizationTypes: [OrganizationType.WASTE_OUTLET],
    tags: ['branch', 'analysis', 'sites', 'compare', 'pdf'],
    children: ['branch-analysis-overview', 'compare-branches'],
  },
  {
    id: 'branch-analysis-overview',
    title: 'Branch analysis overview',
    summary: 'Open Branch analysis, pick a month, and review summary cards and branch cards.',
    content:
      '1. Open Branch analysis in the sidebar (Overview section).\n\n2. Choose the reporting month from the period dropdown in the page header.\n\n3. Review the summary cards for pickup orders, total waste, completed waste, revenue, and diversion rate.\n\n4. Scroll to the branch cards — each card shows how one collection site performed in the selected month.\n\nTip: You only see branches your role can access. Unassigned waste may appear as its own card when you have full site access.',
    category: 'dashboard_kpis',
    organizationTypes: [OrganizationType.WASTE_OUTLET],
    tags: ['branch', 'analysis', 'overview', 'month', 'cards'],
  },
  {
    id: 'compare-branches',
    title: 'Compare branches & export PDF',
    summary: 'Select branches to compare side by side, then export a PDF report.',
    content:
      '1. On Branch analysis, click Compare branches in the header.\n\n2. Select at least two branches (or use Select all), then click Next.\n\n3. Review the charts and detailed comparison table for the selected month.\n\n4. Click Export PDF to download the comparison report, or Back to change your selection.\n\nTip: Use Go to Compare branches from this article for a short guided walkthrough inside the comparison window.',
    category: 'dashboard_kpis',
    organizationTypes: [OrganizationType.WASTE_OUTLET],
    tags: ['branch', 'compare', 'pdf', 'export', 'charts'],
  },
  {
    id: 'organizations',
    title: 'Organizations',
    summary: 'View and manage organization records.',
    content:
      'The Organizations page lists organizations you can access. Administrators can add, edit, or remove organizations.\n\nSelect a guide below for step-by-step instructions.',
    category: 'organizations_users',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['organizations', 'admin'],
    children: ['organizations-overview', 'manage-organization', 'organization-logo'],
  },
  {
    id: 'organizations-overview',
    title: 'Organizations Overview',
    summary: 'Browse the organizations list.',
    content:
      '1. Open Organizations in the sidebar.\n\n2. Review the table showing organization name, type, and contact details.\n\n3. Use the sidebar organization selector to switch your active organization without leaving the current page.',
    category: 'organizations_users',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['organizations', 'list'],
  },
  {
    id: 'manage-organization',
    title: 'Edit Organization',
    summary: 'Update an existing organization record.',
    content:
      '1. Go to Organizations.\n\n2. Open the row action menu on the organization you want to update.\n\n3. Edit the name, type (Recycler, Waste Mover, or Waste Outlet), contact details, industry tags, Google Maps URL, or notes as needed.\n\n4. Save your changes.\n\nTip: Use the sidebar organization selector to switch your active organization before editing a different record. To change the logo, see Organization Logo.',
    category: 'organizations_users',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['organizations', 'edit', 'admin'],
  },
  {
    id: 'organization-logo',
    title: 'Organization Logo',
    summary: 'Upload or update your organization logo.',
    content:
      '1. Open Organizations in the sidebar.\n\n2. Click Edit on your organization to open the details dialog.\n\n3. In the Organization logo section, choose an image file to upload (or replace the current logo).\n\n4. Optionally remove the logo if you no longer need one.\n\n5. Click Update to save. The logo appears in the organizations list and related views after saving.\n\nTip: Use a clear square or landscape logo so it displays well in the sidebar and tables.',
    category: 'organizations_users',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['organizations', 'logo', 'branding', 'upload'],
  },
  {
    id: 'approvals',
    title: 'Approvals',
    summary: 'Review and decide on pending approval requests for your organization.',
    content:
      '1. Open Approvals in the sidebar.\n\n2. Use the Inbox tab to see requests waiting for a decision. The History tab shows approved, rejected, and cancelled requests.\n\n3. Click Review on a request to open its details — justification, submitter, workflow step, and activity.\n\n4. If you can approve: choose Approve or Reject. When rejecting, provide a clear reason so the submitter knows what to change.\n\n5. If you submitted a request that is still pending, you can Cancel request from the detail view.\n\n6. Use Refresh to reload the list after changes.\n\nTip: Approvals are scoped to the organization selected in the sidebar. You need the right permissions to approve or submit requests.',
    category: 'organizations_users',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['approvals', 'inbox', 'workflow', 'review'],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    summary: 'View alerts and manage read status.',
    content:
      'Notifications keep you informed about platform activity. Open the Notifications item in the sidebar to review unread items.\n\nSelect a guide below for step-by-step instructions.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['notifications', 'alerts'],
    children: ['notification-bell', 'mark-notifications-read'],
  },
  {
    id: 'notification-bell',
    title: 'Open Notifications',
    summary: 'Open and browse notifications from the sidebar.',
    content:
      '1. Click the Notifications item in the sidebar footer.\n\n2. The badge shows how many unread notifications you have.\n\n3. Scroll the list to read recent alerts for your organization.\n\n4. Click outside the menu or press Escape to close it.\n\nNotifications are scoped to the organization currently selected in the sidebar.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['notifications', 'bell'],
  },
  {
    id: 'mark-notifications-read',
    title: 'Mark Notifications as Read',
    summary: 'Clear unread items individually or in bulk.',
    content:
      '1. Open the notification bell menu.\n\n2. Click a single notification to mark it as read — it disappears from the unread list.\n\n3. Use Mark all as read to clear every unread notification at once.\n\n4. The badge count updates immediately after items are marked read.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['notifications', 'read'],
  },
  {
    id: 'user-management',
    title: 'User Management',
    summary: 'Manage employees, roles, collection sites, and access permissions.',
    content:
      'The Users page lets administrators and org managers view team members, assign roles, and control which branches and features each employee can access.\n\nSelect a guide below for step-by-step instructions and permission reference material.',
    category: 'organizations_users',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['users', 'permissions', 'roles', 'employees'],
    children: ['add-user', 'users-overview', PERMISSIONS_HANDBOOK_ARTICLE_ID],
  },
  {
    id: 'add-user',
    title: 'Add User',
    summary: 'Register a new team member with account details, password, and role.',
    content:
      '1. Open Users in the sidebar.\n\n2. Click Add user to open the registration form.\n\n3. In Account, enter the employee email and phone number.\n\n4. In Password, set a login password and confirm it.\n\n5. Optionally fill Personal details for HR and reporting.\n\n6. In Role & permissions, choose a role preset (Admin, Operations manager, Staff, or Read-only), or enable Advanced permissions. Assign collection sites when required.\n\n7. Click Save. The new user can sign in with the email and password you set.\n\nTip: After saving, use Managing Employees to edit roles or sites later. See Permission reference for what each role can access.',
    category: 'organizations_users',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['users', 'add', 'register', 'employees', 'team'],
  },
  {
    id: 'users-overview',
    title: 'Managing Employees',
    summary: 'Edit and assign roles to existing organization members.',
    content:
      '1. Open Users in the sidebar.\n\n2. Use search and filters to find employees by name, role, or status.\n\n3. Click the edit icon to open the employee page with account details, collection sites, and Role & access.\n\n4. Choose a role preset (Admin, Operations manager, Staff, Read-only) or enable Advanced permissions for fine-grained control.\n\n5. For waste outlets, assign collection sites — all sites uses * in permissions; specific sites limit branch access.\n\n6. Save changes. The employee must log in again for permission changes to take effect.\n\nTip: Use Permission reference on the Role & access card to understand what each action and entity means.',
    category: 'organizations_users',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['users', 'edit', 'roles'],
  },
  {
    id: PERMISSIONS_HANDBOOK_ARTICLE_ID,
    title: 'Permission Reference',
    summary: 'Handbook for permission actions, entities, role presets, and UI gating.',
    content: buildPermissionsHandbookArticleContent(),
    category: 'organizations_users',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['permissions', 'roles', 'access', 'security', 'handbook'],
  },
  {
    id: 'account-settings',
    title: 'Account & Preferences',
    summary: 'Change the interface language, switch theme, and manage your account.',
    content:
      'Open the account menu from your profile at the bottom of the sidebar to change the interface language, switch between light and dark mode, and sign out. Your profile shows your username and active organization.\n\nSelect a guide below for step-by-step instructions.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['account', 'language', 'theme', 'appearance', 'preferences', 'settings'],
    children: ['change-language', 'appearance-theme'],
  },
  {
    id: 'change-language',
    title: 'Change Language',
    summary: 'Switch the platform language across the supported languages.',
    content:
      '1. Click your profile at the bottom of the sidebar to open the account menu.\n\n2. Click Languages.\n\n3. Choose your language from the list: English, Arabic, Urdu, Hindi, Bengali, Sinhala, or Nepali.\n\n4. The interface updates instantly, and your choice is remembered the next time you sign in.\n\nNote: Arabic and Urdu automatically switch the layout to right-to-left (RTL) for comfortable reading. The interface and Help content are available in all supported languages.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['language', 'arabic', 'urdu', 'rtl', 'localization', 'translation'],
  },
  {
    id: 'appearance-theme',
    title: 'Light & Dark Mode',
    summary: 'Switch between light and dark appearance.',
    content:
      '1. Click your profile at the bottom of the sidebar to open the account menu.\n\n2. Use the Dark mode switch to toggle between the light and dark themes.\n\n3. Your preference is saved automatically and applied on every visit.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['theme', 'dark mode', 'light mode', 'appearance'],
  },
];

const CHILD_ARTICLE_IDS = new Set(
  KNOWLEDGE_BASE_ARTICLES.flatMap((article) => article.children ?? [])
);

export const getKnowledgeArticlesForOrganizationType = (
  organizationType: OrganizationType
): KnowledgeArticle[] =>
  KNOWLEDGE_BASE_ARTICLES.filter((article) => article.organizationTypes.includes(organizationType));

export const getTopLevelKnowledgeArticlesForOrganizationType = (
  organizationType: OrganizationType
): KnowledgeArticle[] =>
  getKnowledgeArticlesForOrganizationType(organizationType).filter(
    (article) => !CHILD_ARTICLE_IDS.has(article.id)
  );

export const getKnowledgeArticleById = (id: string): KnowledgeArticle | undefined =>
  KNOWLEDGE_BASE_ARTICLES.find((article) => article.id === id);

export const getKnowledgeArticleChildren = (
  article: KnowledgeArticle,
  organizationType?: OrganizationType
): KnowledgeArticle[] => {
  if (!article.children?.length) return [];
  // When no org type is passed, return every child (Help CTAs have no org gate).
  if (!organizationType) {
    return article.children
      .map((childId) => getKnowledgeArticleById(childId))
      .filter((child): child is KnowledgeArticle => Boolean(child));
  }
  const allowed = new Set(
    getKnowledgeArticlesForOrganizationType(organizationType).map((item) => item.id)
  );
  return article.children
    .map((childId) => getKnowledgeArticleById(childId))
    .filter((child): child is KnowledgeArticle => Boolean(child && allowed.has(child.id)));
};

export const getKnowledgeArticleParentId = (articleId: string): string | undefined => {
  const parent = KNOWLEDGE_BASE_ARTICLES.find((article) => article.children?.includes(articleId));
  return parent?.id;
};
